/**
 * FilterToolbar.jsx
 * CCH Care — Phase 2 — Layer 4
 * Last Modified: 2026-03-26
 *
 * Search, filter, sort, and view mode controls.
 */
import { useState, useEffect, useRef } from 'react';
import { COLORS } from '../../../config/constants';

const STATUS_OPTIONS = [
  { key: 'overdue',     label: 'Overdue',     color: COLORS.danger   },
  { key: 'in_progress', label: 'In Progress', color: COLORS.warning  },
  { key: 'complete',    label: 'Complete',    color: COLORS.accent   },
  { key: 'not_started', label: 'Not Started', color: COLORS.textMuted },
];

const ALERT_TYPE_OPTIONS = [
  { key: 'fall_risk',        label: 'Fall Risk' },
  { key: 'dnr',              label: 'DNR'       },
  { key: 'diet_restriction', label: 'Diet'      },
  { key: 'diabetic',         label: 'Diabetic'  },
  { key: 'isolation',        label: 'Isolation' },
];

const SORT_OPTIONS = [
  { key: 'urgency',     label: 'Urgency'        },
  { key: 'room',        label: 'Room Number'    },
  { key: 'adl_percent', label: 'ADL Completion' },
];

export default function FilterToolbar({
  searchQuery, onSearchChange,
  filters, onToggleFilter, onClearFilters, activeFilterCount,
  sortBy, onSortChange,
  viewMode, onViewModeChange,
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const searchRef = useRef(null);

  // Focus the input programmatically when search opens (avoids autoFocus warning)
  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);

  return (
    <div style={{
      background: COLORS.surface,
      borderBottom: `1px solid ${COLORS.border}`,
      position: 'sticky',
      top: 56,
      zIndex: 30,
    }}>
      {/* Toolbar Row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px' }}>
        {searchOpen ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              ref={searchRef}
              type="text"
              placeholder="Name or room..."
              value={searchQuery}
              onChange={e => onSearchChange(e.target.value)}
              style={{
                flex: 1, padding: '8px 12px', borderRadius: 8,
                border: `1px solid ${COLORS.border}`, fontSize: 14, outline: 'none',
              }}
            />
            <button
              onClick={() => { setSearchOpen(false); onSearchChange(''); }}
              style={iconBtnStyle}
            >
              ✕
            </button>
          </div>
        ) : (
          <>
            <button onClick={() => setSearchOpen(true)} style={iconBtnStyle} title="Search">
              🔍
            </button>

            <button
              onClick={() => setFilterOpen(!filterOpen)}
              style={{
                ...iconBtnStyle,
                background: filterOpen ? COLORS.primaryLight : undefined,
                position: 'relative',
              }}
              title="Filter"
            >
              ☰
              {activeFilterCount > 0 && (
                <span style={{
                  position: 'absolute', top: -4, right: -4,
                  background: COLORS.danger, color: '#fff',
                  fontSize: 10, fontWeight: 700,
                  width: 16, height: 16, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {activeFilterCount}
                </span>
              )}
            </button>

            <select
              value={sortBy}
              onChange={e => onSortChange(e.target.value)}
              style={{
                padding: '8px 12px', borderRadius: 8,
                border: `1px solid ${COLORS.border}`, fontSize: 13,
                background: COLORS.surface, color: COLORS.text, cursor: 'pointer',
              }}
            >
              {SORT_OPTIONS.map(opt => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>

            <div style={{ flex: 1 }} />

            <button
              onClick={() => onViewModeChange(viewMode === 'list' ? 'grid' : 'list')}
              style={iconBtnStyle}
              title={viewMode === 'list' ? 'Grid view' : 'List view'}
            >
              {viewMode === 'list' ? '▦' : '☰'}
            </button>
          </>
        )}
      </div>

      {/* Filter Dropdown */}
      {filterOpen && (
        <div style={{ padding: '8px 16px 12px', borderTop: `1px solid ${COLORS.border}` }}>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, marginBottom: 6, textTransform: 'uppercase' }}>
              Status
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {STATUS_OPTIONS.map(opt => {
                const active = filters.status.includes(opt.key);
                return (
                  <button key={opt.key} onClick={() => onToggleFilter('status', opt.key)} style={{
                    padding: '6px 12px', borderRadius: 20,
                    border: `1px solid ${active ? opt.color : COLORS.border}`,
                    background: active ? opt.color + '18' : COLORS.surface,
                    color: active ? opt.color : COLORS.textMuted,
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, marginBottom: 6, textTransform: 'uppercase' }}>
              Alert Type
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {ALERT_TYPE_OPTIONS.map(opt => {
                const active = filters.alertType.includes(opt.key);
                return (
                  <button key={opt.key} onClick={() => onToggleFilter('alertType', opt.key)} style={{
                    padding: '6px 12px', borderRadius: 20,
                    border: `1px solid ${active ? COLORS.primary : COLORS.border}`,
                    background: active ? COLORS.primaryLight : COLORS.surface,
                    color: active ? COLORS.primary : COLORS.textMuted,
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            onClick={() => onToggleFilter('newAdmitsOnly')}
            style={{
              padding: '6px 12px', borderRadius: 20,
              border: `1px solid ${filters.newAdmitsOnly ? COLORS.primary : COLORS.border}`,
              background: filters.newAdmitsOnly ? COLORS.primaryLight : COLORS.surface,
              color: filters.newAdmitsOnly ? COLORS.primary : COLORS.textMuted,
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}
          >
            New Admits Only
          </button>

          {activeFilterCount > 0 && (
            <button
              onClick={onClearFilters}
              style={{
                marginLeft: 8, padding: '6px 12px', borderRadius: 20,
                border: `1px solid ${COLORS.danger}`, background: 'transparent',
                color: COLORS.danger, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Clear All
            </button>
          )}
        </div>
      )}

      {/* Active filter chips */}
      {!filterOpen && activeFilterCount > 0 && (
        <div style={{ display: 'flex', gap: 6, padding: '0 16px 8px', flexWrap: 'wrap' }}>
          {filters.status.map(s => (
            <Chip key={s} label={s.replace('_', ' ')} onDismiss={() => onToggleFilter('status', s)} />
          ))}
          {filters.alertType.map(a => (
            <Chip key={a} label={a.replace('_', ' ')} onDismiss={() => onToggleFilter('alertType', a)} />
          ))}
          {filters.newAdmitsOnly && (
            <Chip label="New Admits" onDismiss={() => onToggleFilter('newAdmitsOnly')} />
          )}
        </div>
      )}
    </div>
  );
}

function Chip({ label, onDismiss }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '4px 10px', borderRadius: 16,
      background: COLORS.primaryLight, color: COLORS.primary,
      fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
    }}>
      {label}
      <button
        onClick={onDismiss}
        style={{
          background: 'none', border: 'none', color: COLORS.primary,
          cursor: 'pointer', fontSize: 12, padding: 0, lineHeight: 1,
        }}
      >
        ✕
      </button>
    </span>
  );
}

const iconBtnStyle = {
  width: 36, height: 36, borderRadius: 8,
  border: `1px solid ${COLORS.border}`, background: COLORS.surface,
  fontSize: 16, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0,
};
